import { useEffect } from 'react';
import Layout from '@/components/feature/Layout';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import { siteInfo, testimonials, faqs } from '@/mocks/animeData';

export default function AboutPage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as const });
  }, []);

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: `关于${siteInfo.name}`,
    description: siteInfo.description,
    url: 'https://beyondtoon.github.io/about',
    about: {
      '@type': 'Organization',
      name: siteInfo.name,
      description: siteInfo.description,
      foundingDate: siteInfo.founded,
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer service',
        email: siteInfo.email,
      },
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <Layout>
      <SEOHead
        title={`关于${siteInfo.name} - 品牌介绍与联系方式`}
        description={`了解${siteInfo.name}的品牌故事、发展历程与口碑评价。${siteInfo.description.slice(0, 60)}`}
        keywords="91动漫品牌介绍,91动漫联系方式,动漫平台"
        canonicalPath="/about"
        schema={{ ...aboutSchema, ...faqSchema }}
      />

      <div className="pt-20 pb-16">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />

          <section className="mt-6">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
              关于{siteInfo.name}
            </h1>
            <p className="text-sm text-foreground-500 mt-2">{siteInfo.slogan}</p>

            <div className="mt-8 rounded-lg overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Anime%20studio%20workspace%20with%20animation%20drawings%20on%20walls%2C%20warm%20lighting%2C%20creative%20atmosphere%2C%20professional%20creative%20studio%2C%20cozy%20modern%20interior%2C%20anime%20posters%20decorating%20the%20space%2C%20clean%20composition%2C%20horizontal%20banner&width=1200&height=500&seq=about-banner&orientation=landscape"
                alt="91动漫团队"
                className="w-full aspect-[12/5] object-cover object-top"
              />
            </div>
          </section>

          <section className="mt-12">
            <h2 className="text-xl font-heading font-bold text-foreground-950 mb-4">
              <a href="#brand-intro" id="brand-intro">品牌介绍</a>
            </h2>
            <div className="prose prose-sm max-w-none text-foreground-600 leading-relaxed space-y-4">
              <p>
                {siteInfo.name}成立于{siteInfo.founded}，是国内领先的动漫资源在线观看平台。自创立以来，我们始终秉持"让每一个动漫爱好者都能找到属于自己的快乐"的理念，致力于为全球华人动漫爱好者提供最全面、最高清、最便捷的动漫观看服务。
              </p>
              <p>
                我们的团队由一群热爱动漫的年轻人组成，涵盖内容运营、技术开发、产品设计等多个领域的专业人才。正是因为对动漫的共同热爱，才有了{siteInfo.name}的诞生与成长。我们深知动漫爱好者的需求——快速更新、高清画质、流畅播放，这些正是我们不断追求的目标。
              </p>
              <p>
                发展至今，{siteInfo.name}已积累了超过<strong>10万部</strong>动漫资源，覆盖国漫、日漫、美漫三大主要来源，并细分热血冒险、恋爱日常、奇幻玄幻、搞笑喜剧、科幻机战等多个类型。我们的活跃用户已突破<strong>500万</strong>，日均播放量超过<strong>300万次</strong>，用户综合评分高达<strong>4.9分</strong>。
              </p>
              <p>
                未来，{siteInfo.name}将继续深耕动漫领域，不断优化产品体验，拓展更多优质内容，为动漫爱好者构建一个集观看、互动、社区于一体的综合性动漫平台。我们相信，动漫不仅仅是娱乐，更是一种文化的传递与情感的表达。
              </p>
            </div>
          </section>

          <section className="mt-14">
            <h2 className="text-xl font-heading font-bold text-foreground-950 mb-6">
              <a href="#testimonials" id="testimonials">用户口碑</a>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {testimonials.map((t, i) => (
                <div
                  key={i}
                  className="bg-background-50 rounded-lg p-5 border border-background-200/70"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                      <img src={t.avatar} alt={t.name} className="w-full h-full object-cover object-top" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-800 whitespace-nowrap">{t.name}</div>
                      <div className="text-xs text-foreground-500 whitespace-nowrap">{t.role}</div>
                    </div>
                    <div className="ml-auto flex items-center gap-0.5">
                      {Array.from({ length: t.rating }, (_, j) => (
                        <i key={j} className="ri-star-fill text-accent-500 text-xs"></i>
                      ))}
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{t.content}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mt-14">
            <h2 className="text-xl font-heading font-bold text-foreground-950 mb-4">
              <a href="#contact" id="contact">联系我们</a>
            </h2>
            <div className="bg-background-50 rounded-lg p-6 border border-background-200/70">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500 flex-shrink-0">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-800 mb-1 whitespace-nowrap">电子邮箱</h3>
                    <p className="text-sm text-foreground-500 whitespace-nowrap">{siteInfo.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-500 flex-shrink-0">
                    <i className="ri-wechat-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-800 mb-1 whitespace-nowrap">官方微信</h3>
                    <p className="text-sm text-foreground-500 whitespace-nowrap">{siteInfo.wechat}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-500 flex-shrink-0">
                    <i className="ri-qq-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-800 mb-1 whitespace-nowrap">官方QQ群</h3>
                    <p className="text-sm text-foreground-500 whitespace-nowrap">{siteInfo.qq}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500 flex-shrink-0">
                    <i className="ri-time-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-800 mb-1 whitespace-nowrap">工作时间</h3>
                    <p className="text-sm text-foreground-500 whitespace-nowrap">周一至周日 9:00 - 22:00</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="mt-14">
            <h2 className="text-xl font-heading font-bold text-foreground-950 mb-4">
              <a href="#faq" id="faq">常见问题</a>
            </h2>
            <div className="space-y-3">
              {faqs.map((faq, i) => (
                <details key={i} className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden">
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer text-sm font-medium text-foreground-800 hover:text-primary-500 transition-colors list-none">
                    {faq.question}
                    <i className="ri-arrow-down-s-line group-open:rotate-180 transition-transform text-foreground-400"></i>
                  </summary>
                  <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                    {faq.answer}
                  </div>
                </details>
              ))}
            </div>
          </section>
        </div>
      </div>
    </Layout>
  );
}